<script setup>
import { ref, onMounted } from 'vue'
import api from '@/api'
import Swal from 'sweetalert2'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.css'
import { exportPagosExcel } from '@/utils/exportExcel'

const pagos = ref([])
const pagina = ref(1)
const totalPaginas = ref(1)
const limite = 10
const startDate = ref('')
const endDate = ref('')
const cargando = ref(false)


const hoy = new Date()
const primerDia = new Date(hoy.getFullYear(), hoy.getMonth(), 1)
const ultimoDia = new Date(hoy.getFullYear(), hoy.getMonth() + 1, 0)
startDate.value = primerDia.toISOString().split('T')[0]
endDate.value = ultimoDia.toISOString().split('T')[0]


const cargarPagos = async () => {
  try {
    cargando.value = true
    const res = await api.get('/pagos-membresia', {
      params: {
        startDate: startDate.value,
        endDate: endDate.value,
        page: pagina.value,
        limit: limite
      }
    })

    pagos.value = res.data.pagos || []
    totalPaginas.value = res.data.totalPaginas
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.error('Error al obtener pagos:', err)
    Swal.fire('Error', 'No se pudieron cargar los pagos', 'error')
  } finally {
    cargando.value = false
  }
}


const cambiarPagina = (n) => {
  if (n >= 1 && n <= totalPaginas.value) {
    pagina.value = n
    cargarPagos()
  }
}


const filtrar = () => {
  pagina.value = 1
  cargarPagos()
}


const formatearFecha = (f) => new Date(f).toLocaleDateString()

const exportando = ref(false)
const exportarExcel = async (todoFiltrado = false) => {
  try {
    exportando.value = true
    let data = pagos.value
    if (todoFiltrado) {
      // trae todo sin paginar para respetar filtros
      const res = await api.get('/pagos-membresia', {
        params: { startDate: startDate.value, endDate: endDate.value, page: 1, limit: 10000 }
      })
      data = res.data.pagos || []
      if (!data.length) {
        Swal.fire('Sin datos', 'No hay pagos en el rango seleccionado', 'info')
        return
      }
    }
    if (!data.length) {
      Swal.fire('Sin datos', 'No hay pagos en la página actual', 'info')
      return
    }
    await exportPagosExcel(data, { startDate: startDate.value, endDate: endDate.value })
  } catch (e) {
    if (process.env.NODE_ENV !== 'production') console.error(e)
    Swal.fire('Error', 'No se pudo exportar', 'error')
  } finally {
    exportando.value = false
  }
}

onMounted(() => {
  cargarPagos()
  flatpickr('.fecha-input', { dateFormat: 'Y-m-d' })
})
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card p-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 class="mb-0">Pagos de Membresía</h5>
      </div>


      <div class="row mb-3 align-items-end">
        <div class="col-md-3 mb-2 mb-md-0">
          <label>Desde:</label>
          <input v-model="startDate" class="form-control form-control-sm fecha-input" type="text"
            placeholder="YYYY-MM-DD" />
        </div>
        <div class="col-md-3 mb-2 mb-md-0">
          <label>Hasta:</label>
          <input v-model="endDate" class="form-control form-control-sm fecha-input" type="text"
            placeholder="YYYY-MM-DD" />
        </div>
        <div class="col-md-2">
          <button class="btn btn-sm btn-success w-100 mb-0" @click="filtrar">
            <i class="ni ni-zoom-split-in text-white text-sm me-1"></i>
            Filtrar
          </button>
        </div>
        <div class="col-md-3">
          <div class="d-flex gap-2">
            <button class="btn btn-sm btn-outline-success w-100 mb-0" :disabled="exportando" @click="exportarExcel(false)">
              <i class="fa fa-file-excel me-1"></i> Página
            </button>
            <button class="btn btn-sm btn-success w-100 mb-0" :disabled="exportando" @click="exportarExcel(true)">
              <i v-if="!exportando" class="fa fa-download me-1"></i>
              <i v-else class="fa fa-spinner fa-spin me-1"></i> Todo
            </button>
          </div>
          <small class="text-muted">Excel: página vs todo filtrado</small>
        </div>
      </div>


      <div class="table-responsive">
        <table class="table table-sm custom-table text-sm">
          <thead>
            <tr>
              <th>Socio</th>
              <th>Fecha Pago</th>
              <th>Membresía</th>
              <th>Estado</th>
              <th>Recibido</th>
              <th>Monto Aplicado</th>
              <th>Cambio</th>
              <th>Método</th>
              <th>Obs.</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!cargando && pagos.length === 0">
              <td colspan="9" class="text-center">No hay pagos</td>
            </tr>
            <tr v-for="p in pagos" :key="p.id">
              <td>{{ p.usuario?.nombre || '-' }}</td>
              <td>{{ formatearFecha(p.fecha_pago) }}</td>
              <td>{{ p.membresia?.nombre || '-' }}</td>
              <td><span class="badge" :class="p.estado === 'anulado' ? 'bg-secondary' : 'bg-success'">{{ (p.estado || 'confirmado').toUpperCase() }}</span></td>
              <td>${{ Number(p.monto_recibido ?? p.monto_pagado ?? 0).toFixed(2) }}</td>
              <td>${{ Number(p.monto_pagado ?? 0).toFixed(2) }}</td>
              <td>${{ Number(p.cambio ?? 0).toFixed(2) }}</td>
              <td>{{ p.metodo_pago }}</td>
              <td>{{ p.observaciones || '-' }}</td>
            </tr>
          </tbody>
        </table>
      </div>


      <div class="d-flex justify-content-between align-items-center mt-3 px-2">
        <small class="text-muted">
          Página {{ pagina }} de {{ totalPaginas }}
        </small>
        <div>
          <button class="btn btn-outline-secondary btn-sm me-2" :disabled="pagina === 1"
            @click="cambiarPagina(pagina - 1)">
            Anterior
          </button>
          <button class="btn btn-outline-secondary btn-sm" :disabled="pagina === totalPaginas"
            @click="cambiarPagina(pagina + 1)">
            Siguiente
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<style scoped>
.custom-table th,
.custom-table td {
  padding: 0.4rem !important;
  font-size: 0.78rem !important;
  line-height: 1.2;
}

.custom-table thead {
  background-color: #f2f2f2;
  text-transform: uppercase;
}
</style>
